import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Autocomplete,
} from '@mui/material';
import { Eye, EyeOff, Globe } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo1-removebg-preview.png';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import ReactFlagsSelect from 'react-flags-select';

export function Login() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phonePrefix, setPhonePrefix] = useState('+243'); // France par défaut
  const [selectedCountry, setSelectedCountry] = useState('CD');
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { setAuthInfo } = useAuth();

  // Traductions
  const text = {
    fr: {
      login: 'Connexion',
      phone: 'Téléphone',
      password: 'Mot de passe',
      loginButton: 'Se connecter',
      noAccount: 'Vous n\'avez pas de compte ?',
      createAccount: 'Créer un compte',
      selectLanguage: 'Sélectionner la langue',
      loginError: 'Erreur de connexion. Vérifiez vos identifiants.',
      searchCountry: 'Rechercher un pays',
      selectCountry: 'Sélectionner un pays',
      phoneNumber: 'Numéro de téléphone'
    },
    en: {
      login: 'Login',
      phone: 'Phone',
      password: 'Password',
      loginButton: 'Login',
      noAccount: 'Don\'t have an account?',
      createAccount: 'Create account',
      selectLanguage: 'Select language',
      loginError: 'Login error. Check your credentials.',
      searchCountry: 'Search country',
      selectCountry: 'Select country',
      phoneNumber: 'Phone number'
    }
  };

  const currentText = text[language as keyof typeof text];

  const countryOptions = [
    { code: 'CD', name: 'RD Congo', prefix: '+243' },
    { code: 'FR', name: 'France', prefix: '+33' },
    { code: 'BE', name: 'Belgique', prefix: '+32' },
    { code: 'CH', name: 'Suisse', prefix: '+41' },
    { code: 'CA', name: 'Canada', prefix: '+1' },
    { code: 'US', name: 'États-Unis', prefix: '+1' },
    { code: 'UK', name: 'Royaume-Uni', prefix: '+44' },
    { code: 'DE', name: 'Allemagne', prefix: '+49' },
    { code: 'IT', name: 'Italie', prefix: '+39' },
    { code: 'ES', name: 'Espagne', prefix: '+34' },
    { code: 'PT', name: 'Portugal', prefix: '+351' },
    { code: 'MA', name: 'Maroc', prefix: '+212' },
    { code: 'DZ', name: 'Algérie', prefix: '+213' },
    { code: 'TN', name: 'Tunisie', prefix: '+216' },
    { code: 'SN', name: 'Sénégal', prefix: '+221' },
    { code: 'CI', name: 'Côte d\'Ivoire', prefix: '+225' },
    { code: 'CM', name: 'Cameroun', prefix: '+237' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
      const response = await api.login(formattedPhone, password);
      
      const user = {
        _id: response.data._id,
        username: response.data.username,
        phone: response.data.phone,
        role: response.data.role,
        companyId: response.data.companyId._id,
        company: {
          _id: response.data.companyId._id,
          name: response.data.companyId.name,
          currency: response.data.companyId.currency,
          signCurrency: response.data.companyId.signCurrency,
          lang: response.data.companyId.lang,
          country: response.data.companyId.country,
          category: response.data.companyId.category ? {
            _id: response.data.companyId.category._id,
            name: response.data.companyId.category.name,
            nameEnglish: response.data.companyId.category.nameEnglish,
          } : undefined,
          createdAt: response.data.companyId.createdAt,
          updatedAt: response.data.companyId.updatedAt,
        }
      };
      
      setAuthInfo(response.token, user);

      // Vérifier s'il y a une redirection en attente
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        // Redirection selon la catégorie
        if (response.data.companyId.category?.name === "École") {
          // Pour les écoles, rediriger vers le dashboard école
          navigate(`/${language}/school-dashboard`);
        } else if (response.data.companyId.category?.name === "Hotel") {
          // Pour les hôtels, rediriger vers le dashboard hôtel
          navigate(`/${language}/hotel-dashboard`);
        } else {
          // Pour les autres, rediriger vers le dashboard normal
          navigate(`/${language}/dashboard`);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(currentText.loginError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ mb: 4, textAlign: 'center', position: 'relative' }}>
          <img 
            src={logo} 
            alt="Logo" 
            style={{ 
              height: '60px',
              marginBottom: '1rem',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          />
          {/* Sélecteur de langue */}
          <FormControl 
            sx={{ 
              position: 'absolute',
              right: 0,
              top: 0
            }}
          >
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'fr' | 'en')}
              size="small"
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pr: 4,
                },
              }}
            >
              <MenuItem value="fr" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Globe size={16} /> FR
              </MenuItem>
              <MenuItem value="en" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Globe size={16} /> EN
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            borderRadius: 2,
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
            {currentText.login}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.phone}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ width: '150px' }}>
                  <ReactFlagsSelect
                    selected={selectedCountry}
                    onSelect={(code) => {
                      setSelectedCountry(code);
                      const country = countryOptions.find(c => c.code === code);
                      if (country) {
                        setPhonePrefix(country.prefix);
                        setPhone(country.prefix + phone.replace(/^\+\d+/, ''));
                      }
                    }}
                    countries={countryOptions.map(c => c.code)}
                    customLabels={{
                      FR: "France",
                      BE: "Belgique",
                      CH: "Suisse",
                      CA: "Canada",
                      US: "États-Unis",
                      UK: "Royaume-Uni",
                      DE: "Allemagne",
                      IT: "Italie",
                      ES: "Espagne",
                      PT: "Portugal",
                      MA: "Maroc",
                      DZ: "Algérie",
                      TN: "Tunisie",
                      SN: "Sénégal",
                      CI: "Côte d'Ivoire",
                      CM: "Cameroun",
                      CD: "RD Congo"
                    }}
                    placeholder={currentText.selectCountry}
                    searchable
                    searchPlaceholder={currentText.searchCountry}
                  />
                </Box>
                <TextField
                  fullWidth
                  value={phone.replace(phonePrefix, '')}
                  onChange={(e) => {
                    const phoneNumber = e.target.value.replace(/[^0-9]/g, '');
                    setPhone(phonePrefix + phoneNumber);
                    setError('');
                  }}
                  placeholder={currentText.phoneNumber}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {phonePrefix}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentText.password}
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  backgroundColor: '#0D3C4C',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#164B61',
                  },
                }}
              >
                {isLoading ? 'Chargement...' : currentText.loginButton}
              </Button>
            </Box>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {currentText.noAccount}{' '}
                <Typography
                  component="span"
                  sx={{
                    color: 'primary.main',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => navigate('/register')}
                >
                  {currentText.createAccount}
                </Typography>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
